<template>
  <div class="document-editor">
    <div class="document-editor__header">
      <div class="document-title-container">
      <input class="document-editor__title" placeholder="Enter Section Title" contenteditable v-model="sectionTitle">
        <p class="document-title-label">The section title is only visible within the sidebar and is used for navigation purposes.</p>
      </div>
      <div>
        <button @click="backToDocument()" class="back-btn">Back to document</button>
        <button @click="handleSave()" class="save-btn">Save</button>
      </div>
    </div>

    <div class="document-guidance-container">

    </div>

      <tinymce-editor
        v-model="editorContent"
        api-key="rgwuelv76ygmvst2r951kalhen9s0yy7irpy5we625xig9ca"
        :init="{
          menubar: false,
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'help', 'wordcount', 'paste'
          ],
          toolbar: [
            'undo redo | styles | formatselect | bold italic underline strikethrough | forecolor backcolor',
            'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent',
            'table link | removeformat fullscreen code | customInsertButton | customGuidanceButton | customGuidanceButtonEnd | customInsertListItemButton'
          ].join(' | '),
          paste_as_text: true,
          paste_remove_styles: true,
          height: 600,
          content_style: [
            `
            ol.legal-numbering {
              counter-reset: item;
              list-style: none;
              padding-left: 1.5em;
            }
            .guidance {
              background-color: #00158A;
              border-radius: 8px;
              color: #fff;
              padding: 12px;
            }
            .alpha-list-item {
              display: flex;
              flex-direction: row;
            }

            .alpha-label {
              display: flex;
              align-items: flex-start;
              justify-content: flex-start;
              counter-reset: none;
              min-width: 30px;
            }

            .p {
              margin: 0 !important;
            }

            body {
              counter-reset: alpha-counter;
            }
            `
          ],
          style_formats: [
            {
              title: 'Legal Numbering',
              block: 'ol',
              classes: 'legal-numbering'
            }
          ],
          setup: function (editor) {
          editor.ui.registry.addButton('customInsertButton', {
            text: 'Add Image',
            icon: 'image',
            tooltip: 'Insert an image from the media library',
            onAction: () => {
              openMediaLibrary();
            }
          });
          editor.ui.registry.addButton('customInsertListItemButton', {
            text: 'Add List Item',
            icon: 'comment',
            tooltip: 'Insert new list item',
            onAction: () => {
              const selectedContent = editor.selection.getContent({format: 'html'});

              editor.insertContent(`
                <div class='alpha-list-item'>
                  <div class='alpha-label'>(a)</div>
                  <div class='alpha-content-text'>${selectedContent || 'Insert content here...'}</div>
                </div>
              `)

            }
          });
          editor.ui.registry.addButton('customGuidanceButton', {
            text: 'Start Guidance',
            icon: 'comment',
            tooltip: 'Turn highlighted text into guidance.',
            onAction: () => {
              const selectedContent = editor.selection.getContent()
              editor.insertContent(`<div class='guidance'>${selectedContent}</div>`)
            }
          });
          editor.ui.registry.addButton('customGuidanceButtonEnd', {
            text: 'End Guidance',
            icon: 'comment',
            tooltip: 'Turn off highlighted guidance section',
            onAction: () => {
            const currentContent = editor.getContent();
            const updatedContent = `${currentContent}<p>continue typing here...</p>`;
            editor.setContent(updatedContent);
            }
          });


          editorInstance = editor;
        }
        }"
      />
      <div v-if="showImageUploader" class="image-uploader-container">
        <div>
          <ImageUploader  :title="'Image'" :widthOptionEnabled="false" :documentEditorUploader="true"  :desc="'<p>We reccomend not using text in sections that contain an image.</p>'"/>
            <div class="image-uploader-icon-container">
              <button class="btn-cancel" @click="showImageUploader = false">
                Cancel
              </button>
              <button @click="addImage()" class="btn-add">Add</button>
            </div>
        </div>
      </div>

  </div>
</template>

<script>
import { defineComponent, ref, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useDocuments } from "../../composables/useDocuments";
import Editor from "@tinymce/tinymce-vue";
import StickyFooter from "../Library/StickyFooter.vue";
import ImageUploader from "@/components/Library/ImageUploader"


export default defineComponent({
  name: "DocumentEdit",
  components: {
    "tinymce-editor": Editor,
    StickyFooter,
    ImageUploader
  },

  computed: {
    editorContent: {
      get() {
        return this.$store.state.documentation.currentSection.content;
      },
      set(newValue) {
        this.$store.commit("updateSectionContent", {
          id: this.$store.state.documentation.currentSection.id,
          content: newValue
        });
      }
    },
    sectionTitle: {
      get() {
        return this.$store.state.documentation.currentSection.sectionTitle
      },
      set(newValue) {
        this.$store.commit("updateSectionTitle", {
          id: this.$store.state.documentation.currentSection.id,
          content: newValue
        });
      }
    }
  },

  setup() {
    const { patchDocument, getDocument, postDocument } = useDocuments()
    // watch(() => route.params.sectionId, (newSectionId) => {
    //   if (newSectionId) {
    //     this.getDocument(newSectionId);
    //   }
    // });

    return {
      patchDocument,
      getDocument,
      postDocument
    }
  },

  data() {
    return {
      showImageUploader: false,
      editorInstance: null,
      imageURL: process.env.VUE_APP_IMAGEURL,
      toggleCheckbox: false
    };
  },

  methods: {
    handleSave() {
      const isNewDocument = this.$store.state.documentation.isCreatingNewDocument;

      if (isNewDocument) {
        const initSection = [{
          "sectionTitle": this.sectionTitle,
          "content": this.editorContent,
          "children": [],
          "id": this.$store.state.documentation.currentSectionId
        }];
        this.$store.commit("closeCreateNewDocument");
        this.postDocument("New Document", JSON.stringify(initSection));
      } else {
        const currentDocId = this.$store.state.documentation.currentDocumentId;
        const parsedCurrentDoc = JSON.parse(this.$store.state.documentation.currentDocument) || null;
        const currentSectionId = this.$store.state.documentation.currentSectionId;
        const currentDocumentTitle = this.$store.state.documentation.currentDocumentTitle;
        this.findAndUpdateSection(parsedCurrentDoc, currentSectionId, this.editorContent, this.sectionTitle);
        this.patchDocument(currentDocId, currentDocumentTitle, JSON.stringify(parsedCurrentDoc));
        this.getDocument(currentDocId);
      }
    },

    findAndUpdateSection(sections, targetId, newContent, newTitle) {
      for (let i = 0; i < sections.length; i++) {
        if (sections[i].id === targetId) {
          sections[i].content = newContent;
          sections[i].sectionTitle = newTitle;
          return true;
        }
        if (sections[i].children && sections[i].children.length > 0) {
          if (this.findAndUpdateSection(sections[i].children, targetId, newContent, newTitle)) {
            return true;
          }
        }
      }
      return false;
    },

    backToDocument() {
      this.$router.push(`/document/${this.$store.state.documentation.currentDocumentId}`);
      this.getDocument(this.$store.state.documentation.currentDocumentId)
    },

    openMediaLibrary() {
      this.showImageUploader = true
    },

    addImage() {
      if (this.editorInstance) {
        const imageKey = this.$store.state.mediaLibrary.selectedImage.imageKey;
        const imageUrl = `<img src=\"${this.imageURL}/fit-in/150x150/${imageKey}\">`;
        this.editorInstance.insertContent(imageUrl);
        this.$store.commit("removeImage", {
            mediaLibraryID: "",
            imageKey: ""
        });
        this.showImageUploader = false
      } else {
        console.error("Editor instance is not available.");
      }
    },

    setToGuidance(content) {
      const wrappedContent = `<span class="guidance">${content}</span>`;
      return wrappedContent
    }
  },

  watch: {
    toggleCheckbox(newValue) {
      if (newValue) {
        this.editorContent = `<div class="guidance">${this.editorContent}</div>`;
      } else {
        const div = document.createElement("div");
        div.innerHTML = this.editorContent;
        const guidanceDiv = div.querySelector(".guidance");
        if (guidanceDiv) {
          this.editorContent = guidanceDiv.innerHTML.trim();
        }
      }
    },
    "this.$route.params.id"(newVal) {
      if (newVal) {
        this.getDocument(newVal)
      }
    }
  },

  mounted() {
    this.editorContent = this.$store.state.documentation.currentSection.content;
  }
});
</script>

<style scoped lang="scss">
.document-editor {
  display: flex;
  flex-direction: column;
  padding: 48px;
  height: 100%;

  &__title {
    border-bottom: 2px solid #eee;
    margin-bottom: 20px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 24px;

    &:focus {
      color: none;
      outline: none;
    }
  }
}

.document-editor__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.document-guidance-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 12px;
}

.document-checkbox {
  display: flex;
  align-items: center;
  
  & P {
    margin: 0 !important;
    font-size: 16px;
  }
}

.document-guidance-tooltip {
  display: flex;
  background-color: rgba(225, 255, 0, 0.342);
  color: black;
  padding: 10px;
  font-size: 12px;
  border-radius: 8px;
  margin: 0 12px;
  transition: 100ms;
}

.document-title-container {
  display: flex;
  flex-direction: column;
}

.document-title-label {
  background-color: rgba(225, 255, 0, 0.342);
  color: black;
  padding: 10px;
  font-size: 12px;
  border-radius: 8px;
  margin: 0;
}

.loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    font-size: 1.2rem;
    color: #555;
}

.save-btn {
    background: $purple;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-left: 20px;
  }

  .back-btn {
    background: black;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-left: 20px;
  }

  .image-uploader-container {
    position: fixed;
    z-index: 100;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.6);
  }

  .image-uploader-icon-container {
    display: flex;
    justify-content: flex-end;
    cursor: pointer;
  }

  .image-uploader-icon {
    color: white;
    background-color: $purple;
    border-radius: 100%;
    cursor: pointer;
  }

  .btn-add {
    background: $purple;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-left: 20px;
  }
  .btn-cancel {
    background: $black;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-left: 20px;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 100ms;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  /deep/ .alpha-content-text {
      margin: 0 !important;
    }
</style>
